/**
 * CSV parsing and generation utilities.
 * Handles quoted fields, newlines in quotes, and various delimiters.
 */

export interface CSVParseOptions {
  delimiter?: string;
  hasHeader?: boolean;
}

export interface CSVParseResult {
  headers: string[];
  rows: string[][];
  rowCount: number;
}

/**
 * Parse a CSV string into headers and rows.
 */
export function parseCSV(text: string, options: CSVParseOptions = {}): CSVParseResult {
  const { delimiter = ',', hasHeader = true } = options;

  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          currentField += '"';
          i += 2;
        } else {
          inQuotes = false;
          i++;
        }
      } else {
        currentField += char;
        i++;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
        i++;
      } else if (char === delimiter) {
        currentRow.push(currentField.trim());
        currentField = '';
        i++;
      } else if (char === '\n' || char === '\r') {
        currentRow.push(currentField.trim());
        if (currentRow.some(f => f.length > 0)) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
        if (char === '\r' && i + 1 < text.length && text[i + 1] === '\n') {
          i += 2;
        } else {
          i++;
        }
      } else {
        currentField += char;
        i++;
      }
    }
  }

  // Final field/row
  currentRow.push(currentField.trim());
  if (currentRow.some(f => f.length > 0)) {
    rows.push(currentRow);
  }

  if (hasHeader && rows.length > 0) {
    const headers = rows[0];
    return { headers, rows: rows.slice(1), rowCount: rows.length - 1 };
  }

  return {
    headers: rows.length > 0 ? rows[0].map((_, i) => `Column ${i + 1}`) : [],
    rows,
    rowCount: rows.length,
  };
}

/**
 * Generate a CSV string from headers and data rows.
 */
export function generateCSV(headers: string[], rows: Record<string, any>[]): string {
  const escapeField = (value: any): string => {
    const str = value == null ? '' : String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const headerLine = headers.map(escapeField).join(',');
  const dataLines = rows.map(row => headers.map(h => escapeField(row[h])).join(','));

  return [headerLine, ...dataLines].join('\n');
}

/**
 * Trigger a CSV file download in the browser.
 */
export function downloadCSV(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Export data as CSV with automatic download.
 */
export function exportToCSV(headers: string[], rows: Record<string, any>[], filename: string) {
  const csv = generateCSV(headers, rows);
  downloadCSV(csv, filename);
}

/**
 * Map CSV columns to expected fields using fuzzy matching.
 */
export function autoMapColumns(
  csvHeaders: string[],
  expectedFields: { key: string; aliases: string[] }[],
): Record<string, string> {
  const mapping: Record<string, string> = {};

  for (const field of expectedFields) {
    const normalizedAliases = field.aliases.map(a => a.toLowerCase().replace(/[^a-z0-9]/g, ''));

    for (const header of csvHeaders) {
      const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (normalizedAliases.includes(normalizedHeader)) {
        mapping[field.key] = header;
        break;
      }
    }
  }

  return mapping;
}

/**
 * Loan import field definitions for auto-mapping.
 */
export const LOAN_IMPORT_FIELDS = [
  {
    key: 'borrowerName',
    aliases: ['borrower', 'borrower_name', 'borrowerName', 'client', 'customer', 'name'],
  },
  { key: 'borrowerEmail', aliases: ['email', 'borrower_email', 'borrowerEmail', 'client_email'] },
  {
    key: 'borrowerPhone',
    aliases: ['phone', 'borrower_phone', 'borrowerPhone', 'mobile', 'telephone'],
  },
  { key: 'loanType', aliases: ['loan_type', 'loanType', 'type', 'product'] },
  { key: 'principal', aliases: ['principal', 'amount', 'loan_amount', 'loanAmount'] },
  { key: 'interestRate', aliases: ['interest_rate', 'interestRate', 'rate', 'apr'] },
  { key: 'termMonths', aliases: ['term_months', 'termMonths', 'term', 'tenure', 'duration'] },
  { key: 'frequency', aliases: ['payment_frequency', 'paymentFrequency', 'frequency'] },
];

export const BORROWER_IMPORT_FIELDS = [
  { key: 'name', aliases: ['name', 'borrower_name', 'borrowerName', 'full_name', 'client'] },
  { key: 'email', aliases: ['email', 'email_address'] },
  { key: 'phone', aliases: ['phone', 'mobile', 'telephone', 'phone_number'] },
  { key: 'taxId', aliases: ['tax_id', 'taxId', 'ssn', 'tin', 'abn'] },
  { key: 'address', aliases: ['address', 'street_address'] },
  { key: 'city', aliases: ['city', 'suburb', 'town'] },
  { key: 'state', aliases: ['state', 'province', 'region'] },
  { key: 'postcode', aliases: ['postcode', 'zip', 'zip_code', 'postal_code'] },
];
